import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useGetPaymentsByBorrowerQuery } from "../../services/loanPaymentApiSlice";
import {
  useGetScheduleSummaryByPaymentIdQuery,
  useGetAllocationHistoryQuery,
} from "../../services/loanPaymentAllocationApiSlice";
import {
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Check,
  AlertCircle,
  Clock,
  CreditCard,
  Landmark,
  Smartphone,
  TrendingUp,
  Wallet,
  BadgePercent,
  CheckCircle,
  AlertTriangle,
  MinusCircle,
  Eye,
  Layers,
  FileText,
  ArrowRight,
  Search,
  Download,
  Printer,
  X,
  Filter,
} from "lucide-react";

const PaymentAllocationHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const borrowerId = user?.userId || user?.id || user?.borrowerId || null;

  const [expandedPayments, setExpandedPayments] = useState({});
  const [expandedSchedules, setExpandedSchedules] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilter, setSearchFilter] = useState("all");
  const [showSearchFilters, setShowSearchFilters] = useState(false);
  
  // Refs for printing
  const printRef = useRef(null);
  const cardPrintRefs = useRef({});

  const { data: payments = [], isLoading: paymentsLoading, refetch: refetchPayments } =
    useGetPaymentsByBorrowerQuery(borrowerId, {
      skip: !borrowerId,
    });

  // Refetch payments when navigating from payment submission
  useEffect(() => {
    if (location.state?.refresh) {
      refetchPayments();
      navigate(location.pathname, { replace: true });
    }
  }, [location.state?.refresh, refetchPayments, navigate, location.pathname]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    if (!payments.length) return null;
    return payments.reduce(
      (acc, payment) => {
        acc.totalPaid += payment.totalPaid || 0;
        acc.totalOverdue += payment.totalOverdue || 0;
        acc.paidOverdue += payment.paidOverdue || 0;
        acc.balance += payment.balanceAmount || 0;
        return acc;
      },
      { totalPaid: 0, totalOverdue: 0, paidOverdue: 0, balance: 0 }
    );
  }, [payments]);

  // Filter payments based on search
  const filteredPayments = useMemo(() => {
    if (!searchTerm.trim()) return payments;

    return payments.filter((payment) => {
      const searchLower = searchTerm.toLowerCase();
      const loanName = (payment.loanName || "").toLowerCase();
      const paymentId = (payment.paymentId || payment.id || "").toString();
      const totalPayable = (payment.totalPaid || 0).toString();

      switch (searchFilter) {
        case "loanName":
          return loanName.includes(searchLower);
        case "paymentId":
          return paymentId.includes(searchLower);
        case "amount":
          return totalPayable.includes(searchLower);
        default:
          return (
            loanName.includes(searchLower) ||
            paymentId.includes(searchLower) ||
            totalPayable.includes(searchLower)
          );
      }
    });
  }, [payments, searchTerm, searchFilter]);

  const togglePayment = (paymentId) => {
    setExpandedPayments((prev) => ({
      ...prev,
      [paymentId]: !prev[paymentId],
    }));
  };

  const toggleSchedule = (scheduleKey) => {
    setExpandedSchedules((prev) => ({
      ...prev,
      [scheduleKey]: !prev[scheduleKey],
    }));
  };
// Add these functions inside the PaymentAllocationHistory component
const fetchCompletePaymentData = async (paymentId) => {
  try {
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };

    // Fetch schedules for this payment using the correct API endpoint from the slice
    const schedulesResponse = await fetch(
      `https://moneylend.dockyardsoftware.com/PaymentAllocation/GetScheduleSummaryByPaymentId?paymentId=${paymentId}`,
      { headers }
    );
    const schedulesResult = await schedulesResponse.json();
    const schedules = schedulesResult?.ResultSet || [];
    
    // Fetch allocations for each schedule using the correct API endpoint from the slice
    const schedulesWithAllocations = await Promise.all(
      schedules.map(async (schedule) => {
        const allocationsResponse = await fetch(
          `https://moneylend.dockyardsoftware.com/PaymentAllocation/GetAllocationHistory?paymentId=${paymentId}&scheduleId=${schedule.ScheduleId}`,
          { headers }
        );
        const allocationsResult = await allocationsResponse.json();
        const allocations = allocationsResult?.ResultSet || [];
        return {
          ...schedule,
          allocations: allocations || []
        };
      })
    );
    
    return schedulesWithAllocations;
  } catch (error) {
    console.error('Error fetching complete payment data:', error);
    return [];
  }
};

// Update handlePrintAll function
const handlePrintAll = async () => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  // Show loading state
  printWindow.document.write('<html><head><title>Loading...</title></head><body><div style="text-align:center;margin-top:50px;">Loading payment data...</div></body></html>');

  // Fetch complete data for all filtered payments
  const paymentsWithCompleteData = await Promise.all(
    filteredPayments.map(async (payment) => {
      const paymentId = payment.paymentId || payment.id;
      const schedules = await fetchCompletePaymentData(paymentId);
      return {
        ...payment,
        schedules: schedules
      };
    })
  );

  const styles = `
    <style>
      body { font-family: 'Inter', system-ui, -apple-system, sans-serif; padding: 40px; background: white; }
      .print-header { text-align: center; margin-bottom: 40px; }
      .print-header h1 { font-size: 28px; color: #0f172a; margin: 0; }
      .print-header p { color: #64748b; margin: 8px 0 0; }
      .print-date { color: #64748b; font-size: 14px; margin-top: 8px; }
      .summary-section { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 40px; }
      .summary-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; background: linear-gradient(135deg, #f8fafc, #ffffff); }
      .summary-card h3 { font-size: 14px; color: #64748b; margin: 0; }
      .summary-card .amount { font-size: 20px; font-weight: bold; color: #0f172a; margin: 8px 0 0; }
      .payment-card { border: 1px solid #e2e8f0; border-radius: 16px; margin-bottom: 24px; page-break-inside: avoid; }
      .payment-header { padding: 20px; background: linear-gradient(to right, #f8fafc, #ffffff); border-bottom: 1px solid #e2e8f0; }
      .payment-title { font-size: 18px; font-weight: 600; color: #0f172a; }
      .payment-id { color: #64748b; font-size: 14px; }
      .payment-details { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding: 16px 20px; }
      .schedule-card { margin: 16px 20px 20px; border: 1px solid #e2e8f0; border-radius: 12px; }
      .schedule-header { padding: 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
      .allocation-table { width: 100%; border-collapse: collapse; }
      .allocation-table th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 12px; color: #475569; }
      .allocation-table td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
      .status-badge { display: inline-block; padding: 4px 8px; border-radius: 20px; font-size: 11px; font-weight: 500; }
      @media print {
        body { padding: 20px; }
        .summary-section { break-inside: avoid; }
      }
    </style>
  `;

  const summaryHtml = summary ? `
    <div class="summary-section">
      <div class="summary-card">
        <h3>Total Paid</h3>
        <div class="amount">${formatCurrency(summary.totalPaid)}</div>
      </div>
      <div class="summary-card">
        <h3>Total Overdue</h3>
        <div class="amount">${formatCurrency(summary.totalOverdue)}</div>
      </div>
      <div class="summary-card">
        <h3>Paid Overdue</h3>
        <div class="amount">${formatCurrency(summary.paidOverdue)}</div>
      </div>
      <div class="summary-card">
        <h3>Remaining Balance</h3>
        <div class="amount">${formatCurrency(summary.balance)}</div>
      </div>
    </div>
  ` : '';

  const paymentsHtml = paymentsWithCompleteData.map(payment => 
    generatePaymentPrintHtml(payment, formatCurrency, formatDate, getStatusColor, getStatusIcon)
  ).join('');

  const html = `
    <html>
      <head>
        <title>Payment History Report</title>
        ${styles}
      </head>
      <body>
        <div class="print-header">
          <h1>Payment Allocation History</h1>
          <p>Complete payment report with schedules and allocations</p>
          <div class="print-date">Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
        ${summaryHtml}
        <div class="payments-list">
          ${paymentsHtml}
        </div>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 500);
};

// Update handlePrintCard function
const handlePrintCard = async (paymentId) => {
  const payment = payments.find(p => (p.paymentId || p.id) === paymentId);
  if (!payment) return;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  // Show loading state
  printWindow.document.write('<html><head><title>Loading...</title></head><body><div style="text-align:center;margin-top:50px;">Loading payment data...</div></body></html>');

  // Fetch complete data for this payment
  const schedules = await fetchCompletePaymentData(paymentId);
  const paymentWithCompleteData = {
    ...payment,
    schedules: schedules
  };

  const styles = `
    <style>
      body { font-family: 'Inter', system-ui, -apple-system, sans-serif; padding: 30px; background: white; }
      .print-header { text-align: center; margin-bottom: 30px; }
      .print-header h1 { font-size: 24px; color: #0f172a; margin: 0; }
      .print-header p { color: #64748b; margin: 5px 0 0; }
      .print-date { color: #64748b; font-size: 13px; margin-top: 5px; }
      .payment-card { border: 1px solid #e2e8f0; border-radius: 16px; margin-bottom: 24px; }
      .payment-header { padding: 20px; background: linear-gradient(to right, #f8fafc, #ffffff); border-bottom: 1px solid #e2e8f0; }
      .payment-title { font-size: 18px; font-weight: 600; color: #0f172a; }
      .payment-id { color: #64748b; font-size: 14px; }
      .payment-details { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding: 16px 20px; }
      .schedule-card { margin: 16px 20px 20px; border: 1px solid #e2e8f0; border-radius: 12px; }
      .schedule-header { padding: 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
      .allocation-table { width: 100%; border-collapse: collapse; }
      .allocation-table th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 12px; color: #475569; }
      .allocation-table td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
      .status-badge { display: inline-block; padding: 4px 8px; border-radius: 20px; font-size: 11px; font-weight: 500; }
    </style>
  `;

  const paymentHtml = generatePaymentPrintHtml(paymentWithCompleteData, formatCurrency, formatDate, getStatusColor, getStatusIcon, true);

  const html = `
    <html>
      <head>
        <title>Payment Details - ${payment.loanName || `Loan #${payment.loanId}`}</title>
        ${styles}
      </head>
      <body>
        <div class="print-header">
          <h1>Payment Details Report</h1>
          <p>${payment.loanName || `Loan #${payment.loanId}`}</p>
          <div class="print-date">Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
        ${paymentHtml}
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 500);
};
  

  // Helper function to generate payment HTML for printing
  const generatePaymentPrintHtml = (payment, formatCurrency, formatDate, getStatusColor, getStatusIcon, expandAll = true) => {
    const paymentId = payment.paymentId || payment.id;
    
    return `
      <div class="payment-card">
        <div class="payment-header">
          <div style="display: flex; justify-content: space-between; align-items: start;">
            <div>
              <div class="payment-title">${payment.loanName || `Loan #${payment.loanId}`}</div>
              <div class="payment-id">ID: #${paymentId} | Borrower: ${payment.borrowerName || '—'}</div>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 20px; font-weight: bold; color: #0f172a;">${formatCurrency(payment.totalPaid || 0)}</div>
              <div style="font-size: 12px; color: #64748b;">Total Payment</div>
            </div>
          </div>
        </div>
        
        <div class="payment-details">
          <div>
            <div style="font-size: 12px; color: #64748b;">Total Paid</div>
            <div style="font-weight: 600;">${formatCurrency(payment.totalPaid || 0)}</div>
          </div>
          <div>
            <div style="font-size: 12px; color: #64748b;">Balance</div>
            <div style="font-weight: 600;">${formatCurrency(payment.balanceAmount || 0)}</div>
          </div>
          <div>
            <div style="font-size: 12px; color: #64748b;">Overdue</div>
            <div style="font-weight: 600; color: #e11d48;">${formatCurrency(payment.totalOverdue || 0)}</div>
          </div>
          <div>
            <div style="font-size: 12px; color: #64748b;">Paid Overdue</div>
            <div style="font-weight: 600; color: #059669;">${formatCurrency(payment.paidOverdue || 0)}</div>
          </div>
        </div>

        ${generateSchedulesPrintHtml(paymentId, payment.schedules || [], formatCurrency, formatDate, getStatusColor, getStatusIcon, expandAll)}
      </div>
    `;
  };

 // Also update the generateSchedulesPrintHtml function to handle the new data structure
const generateSchedulesPrintHtml = (paymentId, schedules, formatCurrency, formatDate, getStatusColor, getStatusIcon, expandAll) => {
  if (!schedules || schedules.length === 0) {
    return `
      <div style="padding: 20px; text-align: center; color: #64748b;">
        No schedules found for this payment
      </div>
    `;
  }


  return schedules.map(schedule => {

      console.log("Schedules are ", schedules);
    const scheduleId = schedule.ScheduleId || schedule.id;
    const statusClass = schedule.ScheduleStatus?.toLowerCase().includes('paid') ? 'background: #d1fae5; color: #065f46;' :
                       schedule.ScheduleStatus?.toLowerCase().includes('overdue') ? 'background: #fee2e2; color: #991b1b;' :
                       schedule.ScheduleStatus?.toLowerCase().includes('partial') ? 'background: #fef3c7; color: #92400e;' :
                       'background: #f1f5f9; color: #334155;';

    return `
      <div class="schedule-card">
        <div class="schedule-header">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <div style="font-weight: 600;">Installment #${schedule.InstallmentNo || '—'}</div>
              <div style="font-size: 12px; color: #64748b; margin-top: 4px;">Due: ${formatDate(schedule.DueDate)}</div>
            </div>
            <div>
              <span class="status-badge" style="${statusClass}">${schedule.ScheduleStatus || 'Unknown'}</span>
            </div>
          </div>
        </div>
        
        <div style="padding: 12px 16px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; border-bottom: 1px solid #e2e8f0;">
          <div>
            <div style="font-size: 11px; color: #64748b;">Total Due</div>
            <div style="font-weight: 500;">${formatCurrency(schedule.TotalDue || 0)}</div>
          </div>
          <div>
            <div style="font-size: 11px; color: #64748b;">Fees Due</div>
            <div style="font-weight: 500;">${formatCurrency(schedule.FeesDue || 0)}</div>
          </div>
          <div>
            <div style="font-size: 11px; color: #64748b;">Paid So Far</div>
            <div style="font-weight: 500; color: #059669;">${formatCurrency(schedule.PaidSoFar || 0)}</div>
          </div>
          <div>
            <div style="font-size: 11px; color: #64748b;">Remaining</div>
            <div style="font-weight: 500;">${formatCurrency(schedule.RemainingInstallment || 0)}</div>
          </div>
        </div>

        ${generateAllocationsPrintHtml(paymentId, scheduleId, schedule.allocations || [], formatCurrency, formatDate)}
      </div>
    `;
  }).join('');
};
  // Helper function to generate allocations HTML for printing
  const generateAllocationsPrintHtml = (paymentId, scheduleId, allocations, formatCurrency, formatDate) => {
    if (!allocations || allocations.length === 0) {
      return `
        <div style="padding: 20px; text-align: center; color: #64748b; font-size: 13px;">
          No allocations found for this schedule
        </div>
      `;
    }

    return `
      <div style="padding: 16px;">
        <table class="allocation-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount Paid</th>
              <th>To Overdue</th>
              <th>This Allocation</th>
              <th>Method</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            ${allocations.map(allocation => `
              <tr>
                <td>${formatDate(allocation.AllocatedAt || allocation.DueDate)}</td>
                <td style="font-weight: 500;">${formatCurrency(allocation.PaidAmount || 0)}</td>
                <td>${formatCurrency(allocation.ToOverdue || 0)}</td>
                <td>${formatCurrency(allocation.PaidThisAllocation || 0)}</td>
                <td>
                  <span style="background: #f1f5f9; padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                    ${allocation.PaymentMethod || '—'}
                  </span>
                </td>
                <td style="font-family: monospace;">${allocation.ReferenceNo || '—'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(amount || 0);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  const getStatusColor = (status) => {
    if (!status) return "bg-slate-100 text-slate-700";
    const s = status.toLowerCase();
    if (s.includes("paid") || s.includes("completed"))
      return "bg-emerald-100 text-emerald-800";
    if (s.includes("overdue") || s.includes("late"))
      return "bg-rose-100 text-rose-800";
    if (s.includes("partial")) return "bg-amber-100 text-amber-800";
    if (s.includes("pending") || s.includes("processing"))
      return "bg-sky-100 text-sky-800";
    return "bg-slate-100 text-slate-700";
  };

  const getStatusIcon = (status) => {
    if (!status) return <Clock className="w-3.5 h-3.5" />;
    const s = status.toLowerCase();
    if (s.includes("paid") || s.includes("completed"))
      return <CheckCircle className="w-3.5 h-3.5" />;
    if (s.includes("overdue") || s.includes("late"))
      return <AlertTriangle className="w-3.5 h-3.5" />;
    if (s.includes("partial")) return <MinusCircle className="w-3.5 h-3.5" />;
    return <Clock className="w-3.5 h-3.5" />;
  };

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case "cash":
        return <DollarSign className="w-4 h-4" />;
      case "bank transfer":
        return <Landmark className="w-4 h-4" />;
      case "mobile money":
        return <Smartphone className="w-4 h-4" />;
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6 lg:p-8">
      {/* Modern geometric pattern */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-gradient-to-br from-blue-100/30 to-indigo-100/30 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-gradient-to-tr from-emerald-100/30 to-teal-100/30 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-gradient-to-r from-amber-100/20 to-rose-100/20 blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto space-y-8 relative">
        {/* Header with back button and actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/repayment")}
              className="group p-3 hover:bg-white/50 backdrop-blur-sm rounded-2xl transition-all duration-300 hover:shadow-lg"
              aria-label="Back to Repayment"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5 text-slate-600 group-hover:text-blue-600 transition-colors" />
            </button>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                Payment History
              </h1>
              <p className="text-slate-500 mt-2 text-base">
                Track all your payments and allocations in one place
              </p>
            </div>
          </div>

          {/* Export All Button */}
          {payments.length > 0 && (
            <button
              onClick={handlePrintAll}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-800 to-blue-700 hover:bg-blue-600 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
            >
              <Printer className="w-4 h-4" />
              <span>Export All Reports</span>
              <Download className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="relative">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search payments by loan name, payment ID, or amount..."
                className="w-full pl-11 pr-4 py-3 bg-white/70 backdrop-blur-xl border border-white/20 rounded-xl shadow-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            <div className="relative">
              <button
                onClick={() => setShowSearchFilters(!showSearchFilters)}
                className="w-full sm:w-auto px-4 py-3 bg-white/70 backdrop-blur-xl border border-white/20 rounded-xl shadow-lg hover:bg-white/90 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Filter className="w-4 h-4" />
                <span>Filter by: {searchFilter === 'all' ? 'All' : 
                  searchFilter === 'loanName' ? 'Loan Name' :
                  searchFilter === 'paymentId' ? 'Payment ID' : 'Amount'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showSearchFilters ? 'rotate-180' : ''}`} />
              </button>

              {showSearchFilters && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                  {['all', 'loanName', 'paymentId', 'amount'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => {
                        setSearchFilter(filter);
                        setShowSearchFilters(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors ${
                        searchFilter === filter ? 'bg-blue-50 text-blue-600' : 'text-slate-700'
                      }`}
                    >
                      {filter === 'all' ? 'All Fields' :
                       filter === 'loanName' ? 'Loan Name' :
                       filter === 'paymentId' ? 'Payment ID' : 'Amount'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search results count */}
          {searchTerm && (
            <div className="mt-2 text-sm text-slate-500">
              Found {filteredPayments.length} {filteredPayments.length === 1 ? 'payment' : 'payments'}
            </div>
          )}
        </div>

        {/* Glass-morphism Summary Cards */}
        {!paymentsLoading && summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Paid Card - Emerald Glass */}
            <div className="group relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-400/30 to-emerald-500/30 rounded-full blur-2xl -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl shadow-lg text-white">
                    <Wallet className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                    Lifetime
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600">Total Paid</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatCurrency(summary.totalPaid)}
                </p>
                <div className="mt-4 flex items-center text-xs text-slate-500">
                  <TrendingUp className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                  <span>Total lifetime payments</span>
                </div>
              </div>
            </div>

            {/* Total Overdue Card - Rose Glass */}
            <div className="group relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-rose-500/20 to-rose-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-400/30 to-rose-500/30 rounded-full blur-2xl -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-rose-400 to-rose-500 rounded-xl shadow-lg text-white">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-medium text-rose-600 bg-rose-50 px-3 py-1 rounded-full">
                    Outstanding
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600">Total Overdue</p>
                <p className="text-2xl font-bold text-rose-600 mt-1">
                  {formatCurrency(summary.totalOverdue)}
                </p>
                <div className="mt-4 flex items-center text-xs text-slate-500">
                  <Clock className="w-3.5 h-3.5 mr-1 text-rose-500" />
                  <span>Requires attention</span>
                </div>
              </div>
            </div>

            {/* Paid Overdue Card - Blue Glass */}
            <div className="group relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/30 to-indigo-500/30 rounded-full blur-2xl -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl shadow-lg text-white">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                    Cleared
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600">Paid Overdue</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {formatCurrency(summary.paidOverdue)}
                </p>
                <div className="mt-4 flex items-center text-xs text-slate-500">
                  <BadgePercent className="w-3.5 h-3.5 mr-1 text-blue-500" />
                  <span>Successfully resolved</span>
                </div>
              </div>
            </div>

            {/* Remaining Balance Card - Amber Glass */}
            <div className="group relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-orange-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-400/30 to-orange-500/30 rounded-full blur-2xl -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl shadow-lg text-white">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-medium text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                    Current
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600">Remaining Balance</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatCurrency(summary.balance)}
                </p>
                <div className="mt-4 flex items-center text-xs text-slate-500">
                  <TrendingUp className="w-3.5 h-3.5 mr-1 text-amber-500" />
                  <span>Outstanding principal</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments list */}
        {paymentsLoading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/20 p-6 shadow-xl animate-pulse"
              >
                <div className="h-8 bg-slate-200/50 rounded w-1/3 mb-4"></div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="h-4 bg-slate-200/50 rounded"></div>
                  <div className="h-4 bg-slate-200/50 rounded"></div>
                  <div className="h-4 bg-slate-200/50 rounded"></div>
                  <div className="h-4 bg-slate-200/50 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredPayments.length > 0 ? (
          <div className="space-y-6">
            {filteredPayments.map((payment) => (
              <PaymentCard
                key={payment.paymentId || payment.id}
                payment={payment}
                isExpanded={expandedPayments[payment.paymentId || payment.id]}
                onToggle={() => togglePayment(payment.paymentId || payment.id)}
                onPrint={() => handlePrintCard(payment.paymentId || payment.id)}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getStatusColor={getStatusColor}
                getStatusIcon={getStatusIcon}
                getPaymentMethodIcon={getPaymentMethodIcon}
                expandedSchedules={expandedSchedules}
                toggleSchedule={toggleSchedule}
              />
            ))}
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-xl border border-white/20 p-16 text-center shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50"></div>
            <div className="relative">
              <div className="inline-flex p-4 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl shadow-lg text-white mb-4">
                <FileText className="w-8 h-8" />
              </div>
              <p className="text-xl font-semibold text-slate-900">No payments found</p>
              <p className="text-slate-500 mt-2">
                {searchTerm ? 'Try adjusting your search criteria' : 'Your payment history will appear here once you make a payment.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Modern Payment Card Component
const PaymentCard = ({
  payment,
  isExpanded,
  onToggle,
  onPrint,
  formatCurrency,
  formatDate,
  getStatusColor,
  getStatusIcon,
  getPaymentMethodIcon,
  expandedSchedules,
  toggleSchedule,
}) => {
  const paymentId = payment.paymentId || payment.id;
  const { data: schedules = [], isLoading: schedulesLoading } =
    useGetScheduleSummaryByPaymentIdQuery(paymentId, {
      skip: !isExpanded,
    });

  return (
    <div className="space-y-4">
      {/* Main payment card - Modern Dark/Light design */}
      <div className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300">
        {/* Gradient accent line */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-blue-600 to-indigo-600"></div>
        
        <div className="relative p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Left section - Payment Info */}
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg text-white">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {payment.loanName || `Loan #${payment.loanId}`}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Borrower: {payment.borrowerName || "—"} • ID: #{paymentId}
                  </p>
                  
                  {/* Quick stats */}
                  <div className="flex flex-wrap items-center gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">Paid:</span>
                      <span className="text-sm font-bold text-emerald-600">
                        {formatCurrency(payment.paidAmount || 0)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">Balance:</span>
                      <span className="text-sm font-bold text-slate-900">
                        {formatCurrency(payment.balanceAmount || 0)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">Overdue:</span>
                      <span className="text-sm font-bold text-rose-600">
                        {formatCurrency(payment.totalOverdue || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right section - Actions & Amount */}
            <div className="flex flex-col items-end gap-4">
              <div className="text-right">
                <p className="text-3xl font-bold text-slate-900">
                  {formatCurrency(payment.totalPaid || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Total Payment</p>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Print Card Button */}
                <button
                  onClick={onPrint}
                  className="group/btn inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 hover:text-blue-600 font-medium rounded-xl shadow-sm hover:shadow transition-all duration-200"
                  title="Print this payment report"
                >
                  <Printer className="w-4 h-4" />
                </button>

                {/* View Schedules button */}
                <button
                  onClick={onToggle}
                  className="group/btn inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                >
                  <Layers className="w-4 h-4 group-hover/btn:rotate-12 transition-transform" />
                  <span>View Schedules</span>
                  <ArrowRight className={`w-4 h-4 transition-all duration-300 ${
                    isExpanded ? "rotate-90" : "group-hover/btn:translate-x-1"
                  }`} />
                </button>
              </div>
            </div>
          </div>

          {/* Payment summary grid - Only visible when collapsed */}
          {!isExpanded && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Paid</p>
                <p className="text-sm font-bold text-slate-900 mt-1">
                  {formatCurrency(payment.totalPaid || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Balance</p>
                <p className="text-sm font-bold text-slate-900 mt-1">
                  {formatCurrency(payment.balanceAmount || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Overdue</p>
                <p className="text-sm font-bold text-rose-600 mt-1">
                  {formatCurrency(payment.totalOverdue || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Paid Overdue</p>
                <p className="text-sm font-bold text-emerald-600 mt-1">
                  {formatCurrency(payment.paidOverdue || 0)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expanded schedules section with modern design */}
      {isExpanded && (
        <div className="space-y-4 pl-8 border-l-2 border-blue-200">
          {schedulesLoading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-slate-100 rounded-lg"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : schedules.length > 0 ? (
            schedules.map((schedule) => (
              <ScheduleCard
                key={`${paymentId}-${schedule.ScheduleId}`}
                schedule={schedule}
                paymentId={paymentId}
                isExpanded={
                  expandedSchedules[`${paymentId}-${schedule.ScheduleId}`]
                }
                onToggle={() =>
                  toggleSchedule(`${paymentId}-${schedule.ScheduleId}`)
                }
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getStatusColor={getStatusColor}
                getStatusIcon={getStatusIcon}
              />
            ))
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No schedules found</p>
              <p className="text-slate-400 text-sm mt-1">
                This payment doesn't have any associated schedules
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Modern Schedule Card Component
const ScheduleCard = ({
  schedule,
  paymentId,
  isExpanded,
  onToggle,
  formatCurrency,
  formatDate,
  getStatusColor,
  getStatusIcon,
}) => {
  const scheduleId = schedule.ScheduleId || schedule.id;
  const { data: allocations = [], isLoading: allocationsLoading } =
    useGetAllocationHistoryQuery(
      { paymentId, scheduleId },
      { skip: !isExpanded }
    );

  return (
    <div className="space-y-3">
      {/* Schedule card - Modern design */}
      <div className="group relative overflow-hidden rounded-xl bg-white border border-slate-200 shadow-md hover:shadow-lg transition-all duration-300">
        {/* Status indicator line */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${
          schedule.ScheduleStatus?.toLowerCase().includes('paid') ? 'bg-gradient-to-b from-emerald-500 to-emerald-600' :
          schedule.ScheduleStatus?.toLowerCase().includes('overdue') ? 'bg-gradient-to-b from-rose-500 to-rose-600' :
          schedule.ScheduleStatus?.toLowerCase().includes('partial') ? 'bg-gradient-to-b from-amber-500 to-amber-600' :
          'bg-gradient-to-b from-slate-400 to-slate-500'
        }`}></div>
        
        <div className="relative p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left section - Schedule Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg text-white shadow-md">
                  <Calendar className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">
                    Installment #{schedule.InstallmentNo || "—"}
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Due: {formatDate(schedule.DueDate)}
                  </p>
                </div>
              </div>
              
              {/* Quick stats */}
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(schedule.ScheduleStatus)}`}>
                  {getStatusIcon(schedule.ScheduleStatus)}
                  {schedule.ScheduleStatus || "Unknown"}
                </span>
                <span className="text-xs text-slate-500">
                  Due: {formatCurrency((schedule.TotalDue || 0) + (schedule.FeesDue || 0))}
                </span>
                <span className="text-xs text-slate-500">
                  Paid: {formatCurrency(schedule.PaidSoFar || 0)}
                </span>
              </div>
            </div>

            {/* Right section - View Allocations button */}
            <button
              onClick={onToggle}
              className="group/btn inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 hover:text-blue-600 font-medium rounded-lg shadow-sm hover:shadow transition-all duration-200"
            >
              <Eye className="w-4 h-4" />
              <span>View Allocations</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                isExpanded ? "rotate-180" : ""
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded allocations table with modern design */}
      {isExpanded && (
        <div className="ml-6 pl-6 border-l-2 border-indigo-200">
          {allocationsLoading ? (
            <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-10 bg-slate-100 rounded animate-pulse"></div>
                ))}
              </div>
            </div>
          ) : allocations.length > 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        To Overdue
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        This Allocation
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Method
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Reference
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {allocations.map((allocation, idx) => (
                      <tr
                        key={allocation.AllocationId || allocation.Id}
                        className="hover:bg-slate-50/80 transition-colors"
                      >
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {formatDate(allocation.AllocatedAt || allocation.DueDate)}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {formatCurrency(allocation.PaidAmount || 0)}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {formatCurrency(allocation.ToOverdue || 0)}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {formatCurrency(allocation.PaidThisAllocation || 0)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md text-xs font-medium text-slate-700">
                            {allocation.PaymentMethod || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs font-mono">
                          {allocation.ReferenceNo || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No allocations found for this schedule</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentAllocationHistory;